import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Create context
const SidebarContext = createContext();

// Action types
const SIDEBAR_ACTIONS = {
    TOGGLE_COLLAPSED: 'TOGGLE_COLLAPSED',
    SET_COLLAPSED: 'SET_COLLAPSED',
    SET_ANIMATION_ENABLED: 'SET_ANIMATION_ENABLED'
};

// Initial state
const initialState = {
    collapsed: localStorage.getItem('sidebarCollapsed') !== null
        ? localStorage.getItem('sidebarCollapsed') === 'true'
        : true, // Default to collapsed if no preference is saved
    animationEnabled: true
};

// Reducer
const sidebarReducer = (state, action) => {
    switch (action.type) {
        case SIDEBAR_ACTIONS.TOGGLE_COLLAPSED:
            return {
                ...state,
                collapsed: !state.collapsed,
                animationEnabled: action.payload?.animationEnabled ?? true
            };
        case SIDEBAR_ACTIONS.SET_COLLAPSED:
            return {
                ...state,
                collapsed: action.payload.collapsed,
                animationEnabled: action.payload?.animationEnabled ?? true
            };
        case SIDEBAR_ACTIONS.SET_ANIMATION_ENABLED:
            return {
                ...state,
                animationEnabled: action.payload
            };
        default:
            return state;
    }
};

// Provider component
export const SidebarProvider = ({ children }) => {
    const [state, dispatch] = useReducer(sidebarReducer, initialState);
    
    // BroadcastChannel for cross-tab communication
    const broadcastChannel = React.useRef(null);
    
    useEffect(() => {
        // Initialize BroadcastChannel if supported
        if (typeof BroadcastChannel !== 'undefined') {
            broadcastChannel.current = new BroadcastChannel('sidebar-sync');
            
            // Listen for messages from other tabs
            broadcastChannel.current.onmessage = (event) => {
                if (event.data.type === 'SIDEBAR_STATE_CHANGE') {
                    // Update state without animation for cross-tab sync
                    dispatch({
                        type: SIDEBAR_ACTIONS.SET_COLLAPSED,
                        payload: {
                            collapsed: event.data.collapsed,
                            animationEnabled: false
                        }
                    });
                }
            };
        }
        
        // Listen for localStorage changes (fallback for browsers without BroadcastChannel)
        const handleStorageChange = (e) => {
            if (e.key === 'sidebarCollapsed' && e.newValue !== null) {
                const newCollapsed = e.newValue === 'true';
                dispatch({
                    type: SIDEBAR_ACTIONS.SET_COLLAPSED,
                    payload: {
                        collapsed: newCollapsed,
                        animationEnabled: false
                    }
                });
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        
        return () => {
            if (broadcastChannel.current) {
                broadcastChannel.current.close();
            }
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);
    
    // Function to toggle collapsed state (for user interactions)
    const toggleCollapsed = (animationEnabled = true) => {
        const newCollapsed = !state.collapsed;
        
        // Update localStorage
        localStorage.setItem('sidebarCollapsed', newCollapsed.toString());
        
        // Broadcast to other tabs via BroadcastChannel
        if (broadcastChannel.current) {
            broadcastChannel.current.postMessage({
                type: 'SIDEBAR_STATE_CHANGE',
                collapsed: newCollapsed
            });
        }
        
        // Update local state
        dispatch({
            type: SIDEBAR_ACTIONS.TOGGLE_COLLAPSED,
            payload: { animationEnabled }
        });
    };
    
    // Function to set collapsed state directly
    const setCollapsed = (collapsed, animationEnabled = true) => {
        // Update localStorage
        localStorage.setItem('sidebarCollapsed', collapsed.toString());
        
        // Broadcast to other tabs via BroadcastChannel
        if (broadcastChannel.current) {
            broadcastChannel.current.postMessage({
                type: 'SIDEBAR_STATE_CHANGE',
                collapsed: collapsed
            });
        }
        
        // Update local state
        dispatch({
            type: SIDEBAR_ACTIONS.SET_COLLAPSED,
            payload: { collapsed, animationEnabled }
        });
    };
    
    // Auto-collapse on mobile screens
    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 768px)');

        const handleResize = (e) => {
            if (e.matches) {
                // Mobile: auto-collapse without animation
                dispatch({
                    type: SIDEBAR_ACTIONS.SET_COLLAPSED,
                    payload: { collapsed: true, animationEnabled: false }
                });
            }
        };

        // Check on mount
        if (mediaQuery.matches) {
            dispatch({
                type: SIDEBAR_ACTIONS.SET_COLLAPSED,
                payload: { collapsed: true, animationEnabled: false }
            });
        }

        mediaQuery.addEventListener('change', handleResize);
        return () => mediaQuery.removeEventListener('change', handleResize);
    }, []);

    // Re-enable animations after a short delay when animation is disabled
    useEffect(() => {
        if (!state.animationEnabled) {
            const timer = setTimeout(() => {
                dispatch({
                    type: SIDEBAR_ACTIONS.SET_ANIMATION_ENABLED,
                    payload: true
                });
            }, 50); // Small delay to ensure state change is applied without animation
            
            return () => clearTimeout(timer);
        }
    }, [state.animationEnabled]);
    
    const value = {
        collapsed: state.collapsed,
        animationEnabled: state.animationEnabled,
        toggleCollapsed,
        setCollapsed
    };
    
    return (
        <SidebarContext.Provider value={value}>
            {children}
        </SidebarContext.Provider>
    );
};

// Custom hook to use the sidebar context
export const useSidebar = () => {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within a SidebarProvider');
    }
    return context;
};

export default SidebarContext;
